<script lang="ts" setup>
import { IonApp, IonPage } from '@ionic/vue';
import { RouterView, useRoute } from 'vue-router';
import { App } from '@capacitor/app';
import ModalContainer from '@/components/Molecules/Modals/ModalContainer.vue';
import useCheckAppVersionModals from '@/composables/useCheckAppVersionModals';
import useNetworkStatus from '@/composables/useNetworkStatus';
import { computed, onMounted, onUnmounted, watch } from 'vue';
import useDialog from '@/composables/useDialog';
import useSession from '@/composables/useSession';
import useListenAudio from '@/composables/useListenAudio';
import useRecording from '@/composables/useRecording';
import SessionStatus from '@/models/SessionStatus';
import EventType from '@/models/ElasticApmRum/EventType';
import ProgressStatus from '@/models/ProgressStatus';
import useLoader from '@/composables/useLoader';
import useUserAttention from '@/composables/v2/useFullscreen';
import useElasticApmRumMetricsAdapter from '@/composables/v2/metrics/adapters/useElasticApmRumMetricsAdapter';

const {
    showModalIfAppMustUpdate,
    appMustUpdateModal,
    errorAppVersionCheckModal,
    packagedVersion,
    currentVersion,
    errorAppVersionCheckMessage,
} = useCheckAppVersionModals();

const { createNewDialog, openModal, closeModal } = useDialog();
const { resetSession, currentSessionStatus, currentProgressStatus } = useSession();
const { startSpanWithId, stopSpanWithId, sendEvent } = useElasticApmRumMetricsAdapter();
const { stopListen } = useListenAudio();
const { stopListenToRecord } = useRecording;
const { isFullscreen, enterFullscreen, isDistracted, enableUnloadProtection, disableUnloadProtection, setActive } =
    useUserAttention();
const route = useRoute();

const sessionEndedModal = createNewDialog();
const sessionTimedOutModal = createNewDialog();
const sessionPausedModal = createNewDialog();
const userAttentionModal = createNewDialog();

let sessionPausedSpanId: string | undefined;

function onSessionEnded(): void {
    stopListen();
    stopListenToRecord();

    sendEvent(SessionStatus.ENDED, EventType.SESSION_STATUS);
    openModal(sessionEndedModal);
}

function onSessionTimedOut(): void {
    stopListen();
    stopListenToRecord();

    sendEvent(SessionStatus.ENDED, EventType.SESSION_STATUS);
    openModal(sessionTimedOutModal);
}

function onSessionPaused(): void {
    stopListen();
    stopListenToRecord();

    openModal(sessionPausedModal);
    if (!sessionPausedSpanId) {
        sessionPausedSpanId = startSpanWithId(SessionStatus.PAUSED, EventType.SESSION_STATUS);
    }
}

function onSessionResumed(): void {
    closeModal(sessionPausedModal);
    if (sessionPausedSpanId) {
        stopSpanWithId(sessionPausedSpanId);
        sessionPausedSpanId = undefined;
    }
}

function acknowledgeSessionEnded(): void {
    resetSession();
    closeModal(sessionEndedModal);
    closeModal(sessionTimedOutModal);
}

watch(currentSessionStatus, (status) => {
    if (status === SessionStatus.ENDED) {
        onSessionEnded();
    }
    if (status === SessionStatus.TIMED_OUT) {
        onSessionTimedOut();
    }
    if (status === SessionStatus.PAUSED) {
        onSessionPaused();
    }
    if (status === SessionStatus.STARTED && currentProgressStatus.value !== ProgressStatus.PAUSED) {
        onSessionResumed();
    }
});

watch(currentProgressStatus, (status) => {
    if (status === ProgressStatus.PAUSED) {
        onSessionPaused();
    } else if (currentSessionStatus.value === SessionStatus.STARTED) {
        onSessionResumed();
    }
});

// add listener for app state changes using capacitor
App.addListener('appStateChange', ({ isActive }) => {
    if (isActive) {
        // app is in foreground
        showModalIfAppMustUpdate();
    }
});

const { addNetworkStatusListener, networkStatus } = useNetworkStatus();
addNetworkStatusListener();

const offlineModal = createNewDialog();

watch(networkStatus, (newStatus) => {
    if (!newStatus.connected) {
        openModal(offlineModal, false);
    } else {
        closeModal(offlineModal);
    }
});

const { isLoading } = useLoader();
const loadingModal = createNewDialog();

watch(isLoading, (isLoadingStatus) => {
    if (isLoadingStatus) {
        openModal(loadingModal, false);
    } else {
        closeModal(loadingModal);
    }
});

const isTaskRoute = computed(() => typeof route.name === 'string' && route.name.startsWith('tasks.'));
watch(isTaskRoute, (active) => {
    setActive(active);
});
onMounted(() => setActive(isTaskRoute.value));
onUnmounted(() => setActive(false));

const checkIfDistractedModalNeedToBeOpen = (fullscreen, distracted) => {
    if (!isTaskRoute.value) {
        disableUnloadProtection();
        return;
    }

    enableUnloadProtection();
    if (!fullscreen || distracted) {
        openModal(userAttentionModal, false);
    } else {
        closeModal(userAttentionModal);
    }
};

onMounted(() => {
    checkIfDistractedModalNeedToBeOpen(isFullscreen.value, isDistracted.value);
});

watch([isFullscreen, isDistracted], ([fullscreen, distracted]) => {
    checkIfDistractedModalNeedToBeOpen(fullscreen, distracted);
});

watch(
    () => route.name,
    () => checkIfDistractedModalNeedToBeOpen(isFullscreen.value, isDistracted.value),
    { immediate: true }
);
</script>

<template>
    <IonApp>
        <IonPage class="page">
            <RouterView />

            <ModalContainer :dialog-id="offlineModal" :out-side-closable="false" :modal-closable="false">
                <template #texte>
                    Vous êtes hors ligne <br /><br />
                    <div class="text-xl">Demandez de l'aide pour continuer!</div>
                </template>
            </ModalContainer>

            <ModalContainer :dialog-id="appMustUpdateModal" :out-side-closable="false" :modal-closable="false">
                <template #texte
                    >Votre application doit être mise à jour, demandez de l'aide ! <br /><br />
                    <div class="text-xs">
                        version actuelle : {{ currentVersion }}<br />
                        version tablette : {{ packagedVersion }}
                    </div>
                </template>
            </ModalContainer>

            <ModalContainer :dialog-id="errorAppVersionCheckModal" :out-side-closable="false" :modal-closable="false">
                <template #texte
                    >Une erreur est survenue lors de la vérification de la version de l'application, demandez de l'aide
                    !<br /><br />
                    <div class="text-xs">Error: {{ errorAppVersionCheckMessage }}</div>
                </template>
            </ModalContainer>

            <ModalContainer
                class="z-[1002]"
                :dialog-id="loadingModal"
                :out-side-closable="false"
                :modal-closable="false"
            >
                <template #texte> Chargement... </template>
            </ModalContainer>

            <ModalContainer
                :dialog-id="sessionEndedModal"
                :out-side-closable="false"
                btn-right-text="OK"
                :btn-right-function="acknowledgeSessionEnded"
                :modal-closable="false"
            >
                <template #texte>
                    La session est terminée. Merci pour ta contribution !<br />
                    Clique sur ok pour être redirigé vers la page d'accueil.
                </template>
            </ModalContainer>

            <ModalContainer
                :dialog-id="sessionTimedOutModal"
                :out-side-closable="false"
                btn-right-text="OK"
                :btn-right-function="acknowledgeSessionEnded"
                :modal-closable="false"
            >
                <template #texte> Le temps d'activité est terminé. Merci pour ta participation ! </template>
            </ModalContainer>

            <ModalContainer :dialog-id="sessionPausedModal" :out-side-closable="false" :modal-closable="false">
                <template #texte> La session a été mise en pause. Elle reprendra dans quelques instants. </template>
            </ModalContainer>

            <ModalContainer
                :dialog-id="userAttentionModal"
                :out-side-closable="false"
                :modal-closable="false"
                :btn-right-text="!isFullscreen ? 'Activer le plein écran' : undefined"
                :btn-right-function="!isFullscreen ? enterFullscreen : undefined"
            >
                <template #texte>
                    <template v-if="!isFullscreen && isDistracted">
                        Tu as quitté l'écran <strong>et</strong> tu n'es pas en plein écran.<br />
                        Reviens dans l'application et active le plein écran pour continuer.<br /><br />
                        <small>Astuce Windows : appuie sur F11</small><br />
                        <small>Astuce Mac : appuie sur Ctrl + ⌘ + F</small>
                    </template>
                    <template v-else-if="!isFullscreen">
                        L'application doit être utilisée en mode plein écran.<br />
                        Merci d'activer le plein écran pour continuer.<br /><br />
                        <small>Astuce Windows : appuie sur F11</small><br />
                        <small>Astuce Mac : appuie sur Ctrl + ⌘ + F</small>
                    </template>
                    <template v-else-if="isDistracted">
                        Tu as l’air distrait. Reviens dans l’application pour continuer.
                    </template>
                </template>
            </ModalContainer>
        </IonPage>
    </IonApp>
</template>
